'use client'

import gsap from 'gsap'
import { TextPlugin } from 'gsap/TextPlugin'
import { createTransition } from '~/components/page-transition/create-transition'
import { BANNER_TEXT } from '~/libs/constants'
import { desktopVW } from '~/libs/utils'

gsap.registerPlugin(TextPlugin)

const TRANSFORM_OFFSET = 4

type Refs = Record<string, unknown>
type GlobalRefs = {
  banner: { setSpeed: (speed: number) => void }
  header: HTMLDivElement
}

export const onBeforeExit = createTransition(
  ({ info: { to, lenis }, done, globalRefs }) => {
    const curtainText = BANNER_TEXT[to as keyof typeof BANNER_TEXT] || ''
    lenis?.stop()

    const tl = gsap.timeline()
    // Reset the curtain and prepare for the next transition
    tl.set('#curtain', { clearProps: true })
    tl.set('#curtain-content', { clearProps: true })
    tl.set('#curtain-content-bg', { clearProps: true })
    tl.set('.curtain-text-line', { y: 0 })
    tl.set('#curtain .curtain-line', { text: curtainText })
    // Animate the curtain
    tl.fromTo(
      '.curtain-text-line-left',
      {
        x: (index, _, targets) =>
          index === targets.length - 1
            ? `${TRANSFORM_OFFSET * 4}%`
            : `${getLineOffset(index)}%`,
      },
      {
        duration: 2,
        x: (index, _, targets) =>
          index === targets.length - 1
            ? `${TRANSFORM_OFFSET}%`
            : `${getLineOffset(index) - TRANSFORM_OFFSET}%`,
        ease: 'sine.out',
      }
    )
    tl.fromTo(
      '.curtain-text-line-right',
      { x: (index) => `${-getLineOffset(index)}%` },
      {
        duration: 2,
        x: (index) => `${-getLineOffset(index) + TRANSFORM_OFFSET}%`,
        ease: 'sine.out',
      },
      '<'
    )
    tl.fromTo(
      '#curtain-content',
      { translateY: '100%' },
      {
        duration: 2,
        translateY: 0,
        ease: 'power2.out',
      },
      0
    )
    tl.fromTo(
      '#curtain',
      {
        clipPath: 'inset(100% 0 0 0)',
      },
      {
        clipPath: 'inset(0% 0 0 0)',
        duration: 1.4,
        ease: 'sine.inOut',
      },
      '<'
    )
    // Animate the main content
    tl.fromTo(
      ['main', '#webgl'],
      {
        opacity: 1,
        y: 0,
      },
      {
        y: '-50vh',
        opacity: 0,
        duration: 1,
        ease: 'sine.inOut',
      },
      '<'
    )
    // Animate the header
    tl.fromTo(
      globalRefs.header?.current || {},
      { y: 0 },
      { y: '-100%', duration: 0.7, ease: 'power2.inOut' },
      '>'
    )
    // Resume to next transition state
    tl.call(done)
  }
)

export const onBeforeEnter = createTransition<Refs, GlobalRefs>(
  ({ globalRefs, done, info: { lenis, device } }) => {
    lenis?.stop()
    const tl = gsap.timeline()
    // Pause the banner if it exists and prepare for the next transition
    tl.call(() => {
      globalRefs.banner?.current?.setSpeed?.(0)
    })
    tl.set('#marquee', { opacity: 0 })
    // Animate the curtain content
    tl.fromTo(
      '#curtain-content',
      { y: 0 },
      {
        y:
          device === 'desktop'
            ? `-${(lenis?.dimensions.height || 0) - desktopVW(151, window.innerWidth)}`
            : '-240.5vw',
        duration: 1.5,
        ease: 'power2.inOut',
      }
    )
    tl.addLabel('curtain-up', '<')
    tl.fromTo(
      '.curtain-text-line-left',
      {
        x: (index, _, targets) =>
          index === targets.length - 1
            ? `${TRANSFORM_OFFSET}%`
            : `${getLineOffset(index) - TRANSFORM_OFFSET}%`,
      },
      {
        duration: 2,
        x: (index, _, targets) =>
          index === targets.length - 1
            ? `0%`
            : `${getLineOffset(index) - TRANSFORM_OFFSET * 4}%`,
        ease: 'sine.in',
      },
      '<'
    )
    tl.fromTo(
      '.curtain-text-line-right',
      { x: (index) => `${-getLineOffset(index) + TRANSFORM_OFFSET}%` },
      {
        duration: 2,
        x: (index) => `${-getLineOffset(index) + TRANSFORM_OFFSET * 4}%`,
        ease: 'sine.in',
      },
      '<'
    )
    tl.fromTo(
      '.curtain-text-line',
      { y: 0 },
      {
        y: '-200%',
        duration: 1,
        ease: 'power2.inOut',
      },
      '>-1.2'
    )
    tl.fromTo(
      '#curtain-content-bg',
      { opacity: 1 },
      {
        opacity: 0,
        ease: 'expo.out',
      },
      '<'
    )
    console.log(document.querySelector('#webgl'))
    tl.fromTo(
      ['main', '#webgl'],
      { y: '100vh' },
      { y: 0, duration: 1.48, ease: 'power2.inOut' },
      'curtain-up'
    )
    tl.fromTo(
      globalRefs.header?.current || {},
      { y: '-100%' },
      { y: 0, duration: 1, ease: 'expo.out' },
      '>-.5'
    )
    tl.call(done)
  }
)

export const onAfterEnter = createTransition<Refs, GlobalRefs>(
  ({ globalRefs, done, info: { lenis } }) => {
    lenis?.start()
    const tl = gsap.timeline()
    tl.set('#curtain', { opacity: 0 })
    tl.set('#marquee', { opacity: 1 })
    tl.call(() => {
      globalRefs.banner?.current?.setSpeed?.(1)
    })
    tl.call(done)
  }
)

const lineOffsetMap = [
  34, 14, 8, 22, 10, 26, 20, 12, 28, 16, 4, 24, 18, 6, 2, 30, 14, 8, 22, 10, 26,
  20, 12, 28, 16, 4, 24, 18, 6, 2, 30,
]

export const getLineOffset = (index: number) => {
  return lineOffsetMap[index] || 0
}
